import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';

import { useFreecodeContext } from 'hooks/useContext';
import services from 'api/services';
import { IconButtonContainer, ButtonIcon, IconButtonLinkContainer } from './styles';
import { HiddenFileInput } from 'components/FreecodeContent/styles';

const { FreecodeCreatorService } = services;

export default function CreatorButtons() {
  const { test, testImpl, setTest, setTestImpl } = useFreecodeContext();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const hiddenImportElement = useRef(null);

  // TODO implement save
  const save = () => toast.error('Save Not Implemented');

  const exportZip = () => {
    if (!test) return toast.error('Test Not Found');
    if (exporting) return toast.error('Already Exporting');
    setExporting(true);

    const doExport = async () => {
      let link;
      try {
        const response = await FreecodeCreatorService.exportFreecode(test._id, testImpl?._id);

        link = document.createElement('a');
        link.href = 'data:application/zip;base64,' + response;
        link.download = (test.name || 'freecode') + '.zip';
        link.click();
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'Could not export');
      } finally {
        link?.remove?.();
        setExporting(false);
      }
    };
    doExport();
  };

  const importZip = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return toast.error('No file selected');
    if (file.type !== 'application/zip') return toast.error('Can only import zip files');

    if (importing) return toast.error('Already Importing');
    setImporting(true);

    const doImport = async () => {
      try {
        await new Promise((resolve, reject) => {
          try {
            const reader = new FileReader();

            reader.onload = async () => {
              try {
                let response = await FreecodeCreatorService.importFreecode(test._id, testImpl?._id, {
                  zipString: reader.result,
                });

                if (response.error) return reject(response.error.message || 'Upload returned an error');
                if (!response.test || !response.testImpl) return reject('Import response missing data');

                setTest(response.test);
                setTestImpl(response.testImpl);
                toast.success('Import Done');
                resolve();
              } catch (e) {
                reject(e);
              }
            };

            reader.onerror = () => reject('Could Not Read File');
            reader.readAsDataURL(file);
          } catch (e) {
            reject(e);
          }
        });
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'Could not import');
      } finally {
        setImporting(false);
      }
    };
    doImport();
  };

  return (
    <>
      <IconButtonContainer title="Save you current work" onClick={save}>
        <ButtonIcon icon="fa fa-floppy-o"></ButtonIcon>
      </IconButtonContainer>
      <IconButtonContainer title="Export Freecode Zip" onClick={exportZip}>
        <ButtonIcon icon={exporting ? 'fa fa-spinner' : 'fa fa-download'} spinner={exporting}></ButtonIcon>
      </IconButtonContainer>
      <IconButtonContainer title="Import Freecode Zip" onClick={() => hiddenImportElement.current?.click?.()}>
        <ButtonIcon icon={importing ? 'fa fa-spinner' : 'fa fa-upload'} spinner={importing}></ButtonIcon>
      </IconButtonContainer>
      <HiddenFileInput ref={hiddenImportElement} onChange={importZip}></HiddenFileInput>
      <IconButtonLinkContainer
        title="Go to the Wiki"
        href="https://wiki.futureskill.com/index.php/Freecode_creator"
        target="_blank"
        rel="noopener noreferrer"
      >
        <ButtonIcon icon="fa fa-wikipedia-w"></ButtonIcon>
      </IconButtonLinkContainer>
    </>
  );
}
